import { useState, useEffect, useCallback } from 'react';
import { customInstance } from '@edison/api-client-react';
import {
  AlertCircle,
  RefreshCw,
  FileText,
  FileCode,
  Map,
  Lightbulb,
  Bug,
  ShieldCheck,
  GitBranch,
  Sparkles,
  AlertTriangle,
  Link2,
  type LucideIcon,
} from 'lucide-react';
import { formatLabel } from '@/lib/utils.js';
import { SlidePanel } from '@/components/ui/slide-panel.js';

// ── Endpoint contract (GET /api/projects/:id/documents) ─────────────────────
// Kept as a thin, self-contained view over the durable endpoint so the in-flight
// UI overhaul can restyle this cheaply without touching the data layer.
interface DocLink {
  linkType: string;
  targetId: string;
  targetTitle: string | null;
}
interface TfaReview {
  state: string;
  reason: string | null;
}
interface DocItem {
  id: string;
  type: string;
  title: string;
  // Real content date (commit/meeting date) — what the timeline shows. Falls back to
  // createdAt (ingestion time) for older rows ingested before sourceDate existed.
  date?: string;
  createdAt: string;
  author: { id: string | null } | null;
  changeKind: string | null;
  whyInferred: string | null;
  signals: unknown;
  diffSummary: { filePath: string | null; patch: string };
  links: DocLink[];
  tfaReview: TfaReview | null;
}
interface DocumentsPayload {
  items: DocItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

// ── Type → colour / icon / label map ────────────────────────────────────────
// One small const map: every type gets a Lucide icon and a human text label
// (accessibility §6.2 — status is never conveyed by colour alone).
interface TypeMeta {
  label: string;
  Icon: LucideIcon;
  /** node dot colour on the left rail */
  node: string;
  /** badge (icon + text) colour */
  badge: string;
}

const TYPE_META: Record<string, TypeMeta> = {
  prd_document: { label: 'PRD', Icon: FileText, node: 'bg-violet-500', badge: 'bg-violet-100 text-violet-800 border-violet-200' },
  technical_spec: { label: 'Spec', Icon: FileCode, node: 'bg-blue-500', badge: 'bg-blue-100 text-blue-800 border-blue-200' },
  roadmap_snapshot: { label: 'Roadmap', Icon: Map, node: 'bg-cyan-500', badge: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
  decision_record: { label: 'Decision', Icon: Lightbulb, node: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  bug_log: { label: 'Bug', Icon: Bug, node: 'bg-rose-500', badge: 'bg-rose-100 text-rose-800 border-rose-200' },
  audit_report: { label: 'Audit', Icon: ShieldCheck, node: 'bg-amber-500', badge: 'bg-amber-100 text-amber-800 border-amber-200' },
  engagement_signal: { label: 'Engagement', Icon: GitBranch, node: 'bg-teal-500', badge: 'bg-teal-100 text-teal-800 border-teal-200' },
};

function metaFor(type: string): TypeMeta {
  return (
    TYPE_META[type] ?? {
      label: formatLabel(type),
      Icon: FileText,
      node: 'bg-slate-400',
      badge: 'bg-slate-100 text-slate-700 border-slate-200',
    }
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function TypeBadge({ type }: { type: string }) {
  const { label, Icon, badge } = metaFor(type);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${badge}`}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  );
}

interface DocumentTimelineProps {
  projectId: string;
}

export function DocumentTimeline({ projectId }: DocumentTimelineProps) {
  const [payload, setPayload] = useState<DocumentsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState<DocItem | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    customInstance<{ data: DocumentsPayload }>({
      url: `/api/projects/${projectId}/documents`,
      method: 'GET',
      params: { page: 1, limit: 20 },
    })
      .then((res) => setPayload(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Loading: skeleton rows, never a spinner (§6.4) ─────────────────────────
  if (loading) {
    return (
      <div className="space-y-3" data-testid="document-timeline-loading">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex gap-3">
            <div className="h-3 w-3 rounded-full bg-muted animate-pulse shrink-0 mt-3" />
            <div className="h-20 flex-1 rounded-lg bg-muted animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  // ── Error: message + retry ─────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-destructive">
        <AlertCircle className="h-4 w-4" aria-hidden="true" />
        Failed to load documents.
        <button onClick={load} className="underline ml-2">
          <RefreshCw className="h-3 w-3 inline mr-1" aria-hidden="true" />
          Retry
        </button>
      </div>
    );
  }

  const items = payload?.items ?? [];
  const total = payload?.pagination.total ?? items.length;

  // ── Empty: clear prompt, NO fabricated rows (Rule Zero §2.6) ───────────────
  if (items.length === 0) {
    return (
      <div className="text-center py-12 rounded-lg border border-dashed">
        <FileText className="h-6 w-6 text-muted-foreground mx-auto mb-3" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">No document evidence yet.</p>
        <p className="text-sm text-muted-foreground">
          Plans, PRD changes, and decision records appear here as they&apos;re committed.
        </p>
      </div>
    );
  }

  // ── Success: vertical timeline ─────────────────────────────────────────────
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-3">
        {total} document{total !== 1 ? 's' : ''}
        {payload && total > items.length ? ` · showing first ${items.length}` : ''}
      </p>

      <div className="relative ml-1.5">
        {/* Continuous rail behind the nodes (sibling of <ol> — an <ol> may only contain <li>) */}
        <div className="absolute left-0 top-1 bottom-1 w-px bg-border" aria-hidden="true" />

        <ol>
        {items.map((item) => {
          const { node } = metaFor(item.type);
          return (
            <li key={item.id} className="relative pl-6 pb-4 last:pb-0">
              {/* Node */}
              <span
                className={`absolute left-0 top-3 -translate-x-1/2 h-3 w-3 rounded-full ring-2 ring-background ${node}`}
                aria-hidden="true"
              />

              <button
                type="button"
                data-testid="document-row"
                onClick={() => setSelected(item)}
                className="w-full text-left rounded-lg border bg-background p-3 hover:border-muted-foreground/40 hover:bg-accent/30 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
                aria-label={`Open ${item.title}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <TypeBadge type={item.type} />
                    <span className="text-sm font-medium truncate">{item.title}</span>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{formatDate(item.date ?? item.createdAt)}</span>
                </div>

                {item.whyInferred && (
                  <p className="mt-2 text-xs text-muted-foreground flex items-start gap-1.5">
                    <span className="inline-flex items-center gap-1 text-violet-600 font-medium shrink-0">
                      <Sparkles className="h-3 w-3" aria-hidden="true" />
                      AI-inferred
                    </span>
                    <span className="leading-relaxed">{item.whyInferred}</span>
                  </p>
                )}

                {(item.links.length > 0 || item.tfaReview) && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {item.tfaReview && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                        TFA review recommended
                      </span>
                    )}
                    {item.links.map((l) => (
                      <span
                        key={`${l.linkType}-${l.targetId}`}
                        className="inline-flex items-center gap-1 rounded-full border bg-muted/50 px-2 py-0.5 text-xs text-muted-foreground"
                      >
                        <Link2 className="h-3 w-3" aria-hidden="true" />
                        {formatLabel(l.linkType)}
                        {l.targetTitle ? <span className="text-foreground/70">· {l.targetTitle}</span> : null}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            </li>
          );
        })}
        </ol>
      </div>

      {/* Detail: SlidePanel (never a Dialog/Modal — §6.1) */}
      <SlidePanel open={selected !== null} onClose={() => setSelected(null)} title={selected?.title ?? 'Document'}>
        {selected && (
          <div className="px-5 py-4 space-y-4 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <TypeBadge type={selected.type} />
              <span className="text-xs text-muted-foreground">{formatDate(selected.date ?? selected.createdAt)}</span>
            </div>

            {selected.tfaReview && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="font-medium">TFA review recommended</p>
                  {selected.tfaReview.reason && <p className="text-xs mt-0.5">{selected.tfaReview.reason}</p>}
                </div>
              </div>
            )}

            {selected.changeKind && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Change</p>
                <p>{formatLabel(selected.changeKind)}</p>
              </div>
            )}

            {selected.whyInferred && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                  <Sparkles className="h-3 w-3 text-violet-600" aria-hidden="true" />
                  Why inferred (AI-inferred)
                </p>
                <p className="text-muted-foreground leading-relaxed">{selected.whyInferred}</p>
              </div>
            )}

            {selected.links.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Relationships</p>
                <ul className="space-y-1">
                  {selected.links.map((l) => (
                    <li key={`${l.linkType}-${l.targetId}`} className="flex items-center gap-1.5 text-xs">
                      <Link2 className="h-3 w-3 text-muted-foreground shrink-0" aria-hidden="true" />
                      <span className="text-muted-foreground">{formatLabel(l.linkType)}</span>
                      {l.targetTitle && <span>· {l.targetTitle}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(selected.diffSummary.filePath || selected.diffSummary.patch) && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                  Diff summary
                  {selected.diffSummary.filePath && (
                    <span className="ml-1.5 font-mono font-normal normal-case text-muted-foreground/80">
                      {selected.diffSummary.filePath}
                    </span>
                  )}
                </p>
                {selected.diffSummary.patch ? (
                  <pre className="text-xs whitespace-pre-wrap bg-muted/50 rounded p-2 max-h-72 overflow-y-auto font-mono">
                    {selected.diffSummary.patch}
                  </pre>
                ) : (
                  <p className="text-xs text-muted-foreground">No diff captured.</p>
                )}
              </div>
            )}
          </div>
        )}
      </SlidePanel>
    </div>
  );
}
